import React, { useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { content } from '@configs/content';
import SunburstChart from './SunburstChart';
import TreemapTags from './TreemapTags';
import styles from './index.module.scss';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface StarData {
    id: number;
    left: number;
    top: number;
    size: number;
    animationDelay: number;
    speed: number;
}

const generateStars = (): StarData[] => {
    const starCount = 100;
    const starData: StarData[] = new Array(starCount);

    for (let i = 0; i < starCount; i++) {
        starData[i] = {
            id: i,
            left: Math.random() * 100,
            top: Math.random() * 80,
            size: Math.random() * 2 + 1.5,
            animationDelay: Math.random() * 3,
            speed: Math.random() * 0.3 + 0.7,
        };
    }

    return starData;
};

const TimeLine: React.FC = () => {
    const [stars] = useState<StarData[]>(() => generateStars());
    const [isMobile, setIsMobile] = useState(false);
    const [selectedProvince, setSelectedProvince] = useState<{
        name: string;
        description: string;
        url: string;
    } | null>(null);

    const { defaultTitle: title, middleContent } = content.timeLine.text;
    const { groundBackground, cloudElements } = content.timeLine.visuals;
    const firstInfo = content.timeLine.sunburstChart.firstInfo;

    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const mountainBackgroundRef = useRef<HTMLDivElement>(null);
    const cloud2Ref = useRef<HTMLDivElement>(null);
    const titleContentRef = useRef<HTMLDivElement>(null);
    const starsContainerRef = useRef<HTMLDivElement>(null);
    const middleContentRef = useRef<HTMLDivElement>(null);
    const middleContentInnerRef = useRef<HTMLDivElement>(null);
    const heatmapTextRef = useRef<HTMLDivElement>(null);

    // TreemapTags 数据：从 content 的 keywords 转换为 tags 格式
    const treemapTags = content.timeLine.visualization.keywords.map(kw => ({
        category: kw.name,
        color: kw.color,
        count: kw.value,
        description: kw.description
    }));
    const [selectedTreemapCategory, setSelectedTreemapCategory] = useState<string | null>(null);

    const handleTreemapTagClick = (category: string) => {
        setSelectedTreemapCategory(prev => prev === category ? null : category);
    };

    const handleProvinceSelect = (provinceName: string | null, description?: string, url?: string) => {
        if (provinceName && description && url) {
            setSelectedProvince({
                name: provinceName,
                description,
                url,
            });
        } else {
            setSelectedProvince(null);
        }
    };

    useGSAP(
        () => {
            if (!containerRef.current) return;

            const container = containerRef.current;
            const titleContentElement = titleContentRef.current;
            const mountainElement = mountainBackgroundRef.current;
            const cloud2Element = cloud2Ref.current;
            const starsElement = starsContainerRef.current;
            const middleElement = middleContentRef.current;
            const middleInnerElement = middleContentInnerRef.current;
            const heatmapTextElement = heatmapTextRef.current;

            if (!mountainElement) return;

            const isMobile = window.innerWidth <= 768;

            const heatmapPageElement = container.querySelector('[data-page="heatmap"]');
            const sunburstPageElement = container.querySelector('[data-page="sunburst"]');
            const titleElement = titleContentElement?.querySelector(`.${styles.title}`);
            const dividerElement = titleContentElement?.querySelector(`.${styles.divider}`);

            // 设置初始状态
            if (titleElement) {
                gsap.set(titleElement, {
                    opacity: 0,
                    y: -30,
                });
            }

            if (dividerElement) {
                gsap.set(dividerElement, { scaleX: 0 });
            }

            if (middleElement) {
                gsap.set(middleElement, {
                    xPercent: -50,
                    yPercent: -50,
                    opacity: 0,
                    y: 20,
                });
            }

            if (heatmapPageElement) {
                gsap.set(heatmapPageElement, {
                    xPercent: -50,
                    yPercent: -50,
                    opacity: 0,
                    x: 50,
                });
                (heatmapPageElement as HTMLElement).style.pointerEvents = 'none';
                (heatmapPageElement as HTMLElement).style.zIndex = '-1';
            }

            if (sunburstPageElement) {
                gsap.set(sunburstPageElement, {
                    xPercent: -50,
                    yPercent: -50,
                    opacity: 0,
                    scale: 0.8,
                });
                (sunburstPageElement as HTMLElement).style.pointerEvents = 'none';
                (sunburstPageElement as HTMLElement).style.zIndex = '-1';
            }

            // 入场动画 - 标题和下划线
            const entranceTl = gsap.timeline({
                scrollTrigger: {
                    trigger: container,
                    start: 'top center',
                    end: 'bottom 20%',
                    onEnter: () => entranceTl.restart(),
                    onEnterBack: () => entranceTl.restart(),
                },
            });

            // 标题动画
            if (titleElement) {
                entranceTl.fromTo(
                    titleElement,
                    { opacity: 0, y: -30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: 'power3.out',
                    },
                    0
                );
            }

            // 分割线动画
            if (dividerElement) {
                entranceTl.fromTo(
                    dividerElement,
                    { scaleX: 0 },
                    {
                        scaleX: 1,
                        duration: 0.8,
                        ease: 'power3.out',
                    },
                    0.2
                );
            }

            // ========== TimeLine 滚动距离配置 ==========
            // 简化版：只需调整每个页面的开始时间
            const scrollConfig: {
                page1End: number;
                page2Start: number;
                page2End: number;
                page3Start: number;
                page3End: number;
                page4Start: number;
                page4Duration: number;
                totalDistance?: number;
            } = {
                // 各页面开始时间（数字越大，该页面出现越晚，前面页面停留越久）
                page1End: 2.1,              // 第一页结束时间（山脉移走）
                page2Start: 2.1,            // 第二页开始时间（文案出现）
                page2End: 3.6,              // 第二页结束时间（文案消失）
                page3Start: 4.2,            // 第三页开始时间（热力图出现）
                page3End: 6.0,              // 第三页结束时间（热力图消失）
                page4Start: 6.5,            // 第四页开始时间（旭日图出现）
                page4Duration: 5.0,         // 第四页停留时间（增加这个值让旭日图停留更久）
            };
            
            // 总滚动距离 = 自动计算（确保第四页有足够停留时间）
            const totalDistance = (scrollConfig.page4Start + scrollConfig.page4Duration) * (isMobile ? 800 : 1200);
            scrollConfig.totalDistance = totalDistance;
            // 主时间轴 - 滚动动画
            const mainTl = gsap.timeline({
                scrollTrigger: {
                    trigger: container,
                    start: 'top top',
                    end: `+=${scrollConfig.totalDistance}`,
                    pin: true,
                    scrub: 1,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                },
            });

            const bgColor = { r1: 0, g1: 132, b1: 240, r2: 58, g2: 158, b2: 240, whitePos: 100 };

            const updateBackground = () => {
                container.style.background = `linear-gradient(180deg, 
                    rgb(${Math.round(bgColor.r1)}, ${Math.round(bgColor.g1)}, ${Math.round(bgColor.b1)}) 0%, 
                    rgb(${Math.round(bgColor.r2)}, ${Math.round(bgColor.g2)}, ${Math.round(bgColor.b2)}) 10%, 
                    rgb(255, 255, 255) ${Math.round(bgColor.whitePos)}%)`;
            };

            // ========== 第一页：背景变化动画 ==========
            mainTl.to(
                bgColor,
                {
                    r1: 115,
                    g1: 192,
                    b1: 255,
                    r2: 115,
                    g2: 192,
                    b2: 255,
                    duration: 0.6,
                    ease: 'none',
                    onUpdate: updateBackground,
                },
                0
            );

            if (starsElement) {
                mainTl.to(
                    starsElement,
                    {
                        yPercent: -50,
                        opacity: 0,
                        duration: 0.9,
                        ease: 'power1.out',
                    },
                    0
                );
            }

            /* if (cloud2Element) {
                mainTl.to(
                    cloud2Element,
                    {
                        y: isMobile ? '-100vh' : '-120vh',
                        duration: 0.8,
                        ease: 'power2.out',
                    },
                    0
                );
            } */

            mainTl.to(
                bgColor,
                {
                    whitePos: 40,
                    duration: 0.8,
                    ease: 'power2.out',
                    onUpdate: updateBackground,
                },
                0
            );

            mainTl.to(
                mountainElement,
                {
                    y: isMobile ? '-35vh' : '-80vh',
                    duration: 0.7,
                    ease: 'power2.inOut',
                },
                0.8
            );

            mainTl.to({}, { duration: 0.2 }, scrollConfig.page1End - 0.5);

            mainTl.to(
                mountainElement,
                {
                    x: '-200vw',
                    duration: scrollConfig.page2Start - (scrollConfig.page1End - 0.5) - 0.2,
                    ease: 'none',
                },
                scrollConfig.page1End - 0.3
            );

            /* if (cloud2Element) {
                mainTl.to(
                    cloud2Element,
                    {
                        x: '-260vw',
                        duration: scrollConfig.page2Start - (scrollConfig.page1End - 0.5) - 0.2,
                        ease: 'none',
                    },
                    scrollConfig.page1End - 0.3
                );
            } */

            if (titleContentElement) {
                mainTl.to(
                    titleContentElement,
                    {
                        x: '-196vw',
                        duration: scrollConfig.page2Start - (scrollConfig.page1End - 0.5) - 0.2,
                        ease: 'none',
                    },
                    scrollConfig.page1End - 0.3
                );
            }

            // ========== 第二页：中间文案动画 ==========
            if (middleElement) {
                // 文案淡入
                mainTl.to(
                    middleElement,
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.5,
                        ease: 'power2.out',
                    },
                    scrollConfig.page2Start
                );

                // 移动端：文案内部滚动动画
                if (isMobile && middleInnerElement) {
                    const containerHeight = middleElement.clientHeight || window.innerHeight * 0.7;
                    const contentHeight = middleInnerElement.scrollHeight || 0;
                    const scrollDistance = Math.max(0, contentHeight - containerHeight);

                    if (scrollDistance > 0) {
                        mainTl.to(
                            middleInnerElement,
                            {
                                y: -scrollDistance,
                                duration: 1.5,
                                ease: 'power1.inOut',
                            },
                            scrollConfig.page2Start + 0.6
                        );
                    } else {
                        mainTl.to({}, { duration: 1.5 }, scrollConfig.page2Start + 0.6);
                    }
                }

                // 文案淡出
                mainTl.to(
                    middleElement,
                    {
                        opacity: 0,
                        y: -20,
                        duration: 0.5,
                        ease: 'power2.in',
                    },
                    scrollConfig.page2End
                );

                // 重置内部滚动位置
                if (isMobile && middleInnerElement) {
                    mainTl.set(middleInnerElement, { y: 0 }, scrollConfig.page2End + 0.5);
                }
            }

            // ========== 第三页：热力图动画 ==========
            if (heatmapPageElement) {
                mainTl.to(
                    heatmapPageElement,
                    {
                        opacity: 1,
                        x: 0,
                        duration: 0.5,
                        ease: 'power2.out',
                        onStart: () => {
                            (heatmapPageElement as HTMLElement).style.pointerEvents = 'auto';
                            (heatmapPageElement as HTMLElement).style.zIndex = '20';
                        },
                        onReverseComplete: () => {
                            (heatmapPageElement as HTMLElement).style.pointerEvents = 'none';
                            (heatmapPageElement as HTMLElement).style.zIndex = '-1';
                        },
                    },
                    scrollConfig.page3Start
                );

                mainTl.to(
                    heatmapPageElement,
                    {
                        opacity: 0,
                        x: -50,
                        duration: 0.5,
                        ease: 'power2.in',
                        onStart: () => {
                            (heatmapPageElement as HTMLElement).style.zIndex = '10';
                            if (isMobile && heatmapTextElement) {
                                gsap.set(heatmapTextElement, { y: 0 });
                            }
                        },
                        onComplete: () => {
                            (heatmapPageElement as HTMLElement).style.pointerEvents = 'none';
                            (heatmapPageElement as HTMLElement).style.zIndex = '-1';
                        },
                        onReverseComplete: () => {
                            (heatmapPageElement as HTMLElement).style.pointerEvents = 'auto';
                            (heatmapPageElement as HTMLElement).style.zIndex = '20';
                        },
                    },
                    scrollConfig.page3End
                );
            }

            // ========== 第四页：旭日图动画 ==========
            if (sunburstPageElement) {
                mainTl.to(
                    sunburstPageElement,
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 0.6,
                        ease: 'power2.out',
                        onStart: () => {
                            (sunburstPageElement as HTMLElement).style.pointerEvents = 'auto';
                            (sunburstPageElement as HTMLElement).style.zIndex = '30';
                        },
                        onReverseComplete: () => {
                            (sunburstPageElement as HTMLElement).style.pointerEvents = 'none';
                            (sunburstPageElement as HTMLElement).style.zIndex = '-1';
                        },
                    },
                    scrollConfig.page4Start
                );
            }
        },
        { scope: containerRef }
    );

    return (
        <section ref={containerRef} className={styles.timelineSection} id="timeline">
            <div ref={starsContainerRef} className={styles.starsContainer}>
                {stars.map((star) => (
                    <div
                        key={star.id}
                        className={styles.star}
                        style={{
                            left: `${star.left}%`,
                            top: `${star.top}%`,
                            width: `${star.size}px`,
                            height: `${star.size}px`,
                            animationDelay: `${star.animationDelay}s`,
                        }}
                    />
                ))}
            </div>

            {/* <div ref={cloud2Ref} className={styles.cloudBackground2} style={{ backgroundImage: `url(${cloudElements[1].image})` }} /> */}

            <div
                ref={mountainBackgroundRef}
                className={styles.mountainBackground}
                style={{ backgroundImage: `url(${groundBackground})` }}
            />

            <div ref={titleContentRef} className={styles.titleContent}>
                <h2 className={styles.title}>{title}</h2>
                <div className={styles.divider} />
            </div>

            <div ref={middleContentRef} className={styles.middleContent}>
                <div ref={middleContentInnerRef} className={styles.middleContentInner}>
                    {middleContent.description?.map((line, index) => (
                        <p key={index} className={styles.middleDescription}>
                            {line}
                        </p>
                    ))}
                </div>
            </div>

            <div className={styles.heatmapPage} data-page="heatmap">
                <h3 className={styles.legendTitle}>{content.timeLine.visualization.legendTitle}</h3>
                <div className={styles.heatmapContent}>
                    <div ref={heatmapTextRef} className={styles.heatmapText}>
                        {content.timeLine.visualization.legendText.map((paragraph, index) => {
                            return (
                                <p key={index} className={styles.textContent}>
                                    {paragraph}
                                </p>
                            );
                        })}
                    </div>

                    <div className={styles.heatmapButtons}>
                        <TreemapTags
                            tags={treemapTags}
                            selectedCategory={selectedTreemapCategory}
                            onTagClick={handleTreemapTagClick}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.sunburstPage} data-page="sunburst">
                <div className={styles.sunburstText}>
                    <h3 className={styles.sunburstTitle}>{content.timeLine.sunburstChart.title}</h3>
                    {content.timeLine.sunburstChart.description?.map((paragraph, index) => {
                        return (
                            <p
                                key={index}
                                className={styles.textContent}>
                                {paragraph}
                            </p>
                        );
                    })}

                    <div className={styles.provinceDescription}>
                        <a href={selectedProvince?.url || firstInfo?.url} target="_blank" rel="noopener noreferrer" className={styles.provinceLink}>
                            {selectedProvince?.description || firstInfo?.description}
                        </a>
                    </div>
                </div>

                <div className={styles.sunburstChart}>
                    <SunburstChart
                        data={content.timeLine.sunburstChart.data}
                        tooltipLabels={content.timeLine.sunburstChart.tooltipLabels}
                        onProvinceSelect={handleProvinceSelect}
                    />
                    {content.timeLine.sunburstChart.tips && (
                        <div className={styles.sunburstTips}>{content.timeLine.sunburstChart.tips}</div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default TimeLine;
